import { useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import type { FormState, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import type { ConsentFormData } from '@/schemas/consentSchema';
import { japaneseDateToISO } from '@/utils/dateConverter';
import { arraysEqual, asDateInputString, calculateOneMonthInclusiveEnd } from '../utils';

interface UseConsentTreatmentTermAutoFillParams {
  dirtyFields: FormState<ConsentFormData>['dirtyFields'];
  isContinue: boolean;
  massagePartsSel: unknown[];
  orthomorphiaPartsSel: unknown[];
  serviceType: 'r2' | 'a2';
  setValue: UseFormSetValue<ConsentFormData>;
  watch: UseFormWatch<ConsentFormData>;
}

export const useConsentTreatmentTermAutoFill = ({
  dirtyFields,
  isContinue,
  massagePartsSel,
  orthomorphiaPartsSel,
  serviceType,
  setValue,
  watch,
}: UseConsentTreatmentTermAutoFillParams) => {
  const treatmentTermFrom = watch('treatment_term_from');
  const treatmentTermTo = watch('treatment_term_to');
  const firstTreatmentDate = watch('first_treatment_date');
  const consentDate = watch('consent_date');
  const isContinuationFlag = watch('is_continuation');
  const isSpecialFlag = watch('is_special');
  /**
   * 継続・特殊継続（ルートフラグまたはフォームの is_continuation / is_special）では
   * 同意日を基準日として要加療終期を算出する。
   */
  const continuationLike = isContinue || !!isContinuationFlag || !!isSpecialFlag;
  const hasUserChangedAutoCalcInputs =
    !!dirtyFields.first_treatment_date ||
    !!dirtyFields.consent_date ||
    !!dirtyFields.massage_parts ||
    !!dirtyFields.orthomorphia_parts ||
    !!dirtyFields.is_continuation ||
    !!dirtyFields.is_special;

  const prevInputsRef = useRef<{
    firstTreatmentDate?: string;
    consentDate?: string;
    massageParts?: unknown[];
    orthomorphiaParts?: unknown[];
  }>({});
  const lastAutoTermToRef = useRef<string>('');
  const hasMountedRef = useRef(false);
  // Track previous consent_date to detect user-driven changes (all form modes).
  const editPrevConsentRef = useRef<string>('');
  const editMountedRef = useRef(false);

  const computeAutoTreatmentTermTo = useCallback(
    (baseDate: dayjs.Dayjs) => {
      // R2 変形徒手: one month from the base date, inclusive.
      if (serviceType === 'r2' && orthomorphiaPartsSel.length > 0) {
        return calculateOneMonthInclusiveEnd(baseDate);
      }

      // BD §4.2.2 R62 (15.1 マッサージ) / A2: day <= 15 -> +5 mo, day >= 16 -> +6 mo, end of month.
      const day = baseDate.date();
      const monthsToAdd = day <= 15 ? 5 : 6;
      return baseDate.add(monthsToAdd, 'month').endOf('month').format('YYYY-MM-DD');
    },
    [serviceType, orthomorphiaPartsSel]
  );

  // If user edits treatment_term_to manually, stop auto-calc from then on.
  useEffect(() => {
    if (!hasMountedRef.current) return;
    if (!treatmentTermTo) return;
  }, [treatmentTermTo]);

  useEffect(() => {
    const updatePrev = () => {
      prevInputsRef.current = {
        firstTreatmentDate,
        consentDate,
        massageParts: massagePartsSel,
        orthomorphiaParts: orthomorphiaPartsSel,
      };
    };

    // Initial mount: store values and do nothing.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      updatePrev();
      return;
    }

    const prev = prevInputsRef.current;
    const firstTreatmentChanged =
      prev.firstTreatmentDate !== undefined && prev.firstTreatmentDate !== firstTreatmentDate;
    const consentChanged = prev.consentDate !== undefined && prev.consentDate !== consentDate;
    const massageChanged = !arraysEqual(prev.massageParts ?? [], massagePartsSel);
    const orthomorphiaChanged = !arraysEqual(prev.orthomorphiaParts ?? [], orthomorphiaPartsSel);
    const isTriggered =
      firstTreatmentChanged || consentChanged || massageChanged || orthomorphiaChanged;

    if (!isTriggered) {
      updatePrev();
      return;
    }

    // Edit page reset/API hydration can change watched values. Only run autofill
    // after the user has actually changed related inputs.
    if (!hasUserChangedAutoCalcInputs) {
      updatePrev();
      return;
    }

    // Guard: skip auto-calc when API hydration fills BOTH trigger dates from empty simultaneously
    // (e.g. edit-page reset()). A user changing only one field will pass through.
    const prevFt = asDateInputString(prev.firstTreatmentDate).trim();
    const prevCo = asDateInputString(prev.consentDate).trim();
    const nextFt = asDateInputString(firstTreatmentDate).trim();
    const nextCo = asDateInputString(consentDate).trim();
    const isApiHydration =
      firstTreatmentChanged && consentChanged && !prevFt && !prevCo && !!nextFt && !!nextCo;
    const hasApiTerm = !!(treatmentTermFrom || treatmentTermTo);
    if (isApiHydration && hasApiTerm) {
      updatePrev();
      return;
    }

    const parseDate = (val: unknown) => {
      const s = asDateInputString(val).trim();
      if (!s) return null;
      if (s.includes('年')) {
        const iso = japaneseDateToISO(s);
        return iso ? dayjs(iso) : null;
      }
      return dayjs(s);
    };

    const coDay = parseDate(consentDate);
    const ftDay = continuationLike ? null : parseDate(firstTreatmentDate);

    if (continuationLike) {
      if (!coDay?.isValid()) {
        updatePrev();
        return;
      }
    } else {
      if (!ftDay?.isValid()) {
        updatePrev();
        return;
      }
    }

    // Step 1: Base date
    let baseDate = continuationLike ? (coDay as dayjs.Dayjs) : (ftDay as dayjs.Dayjs);
    if (!continuationLike && coDay?.isValid() && ftDay?.isValid()) {
      if ((ftDay as dayjs.Dayjs).isAfter(coDay.add(1, 'month').subtract(1, 'day'))) {
        baseDate = coDay;
      }
    }

    // Step 2: Offset
    const nextTermTo = computeAutoTreatmentTermTo(baseDate);

    lastAutoTermToRef.current = nextTermTo;
    setValue('treatment_term_to', nextTermTo, { shouldDirty: true, shouldValidate: true });

    updatePrev();
  }, [
    consentDate,
    firstTreatmentDate,
    continuationLike,
    setValue,
    treatmentTermFrom,
    treatmentTermTo,
    massagePartsSel,
    orthomorphiaPartsSel,
    hasUserChangedAutoCalcInputs,
    computeAutoTreatmentTermTo,
  ]);

  /**
   * Auto-fill treatment_term_from = new consent_date when all 3 hold:
   *   1. User changed consent_date
   *   2. treatment_term_from already has a value (not empty)
   *   3. treatment_term_from < new consent_date
   * Runs on all form modes (create / edit / continue) — matches A2 behavior.
   */
  useEffect(() => {
    const nextConsent = asDateInputString(consentDate).trim();

    // On mount: just store the initial value, do nothing.
    if (!editMountedRef.current) {
      editMountedRef.current = true;
      editPrevConsentRef.current = nextConsent;
      return;
    }

    const prevConsent = editPrevConsentRef.current;
    editPrevConsentRef.current = nextConsent;

    // Condition 1: consent_date actually changed (and is not empty).
    if (!nextConsent || nextConsent === prevConsent) return;
    // Only auto-fill after user changed consent_date.
    if (!dirtyFields.consent_date) return;

    // Condition 2: treatment_term_from already has a value.
    const fromStr = asDateInputString(treatmentTermFrom).trim();
    if (!fromStr) return;

    // Condition 3: treatment_term_from < new consent_date.
    const fromDate = new Date(fromStr);
    const consentNewDate = new Date(nextConsent);
    if (isNaN(fromDate.getTime()) || isNaN(consentNewDate.getTime())) return;
    if (fromDate >= consentNewDate) return;

    // All 3 conditions met -> auto-fill treatment_term_from = new consent_date.
    setValue('treatment_term_from', nextConsent, { shouldDirty: true, shouldValidate: true });
  }, [consentDate, treatmentTermFrom, setValue, dirtyFields.consent_date]);
};
